import { ICampaign } from '../interfaces/models/campaign';

/** Statuses that accept new LD Contacts leads. Running campaigns are included on
 *  purpose: a campaign started with no leads sits idle until leads arrive, and the
 *  executors pick up newly enrolled leads on their next run. Canceled campaigns
 *  never run again, so they are excluded. */
const ENROLLABLE_STATUSES = ['draft', 'active', 'paused'];

/** What POST /campaigns/:id/leads/from-contacts reports. */
export interface IContactsEnrollResult {
  enrolled: number;
  alreadyEnrolled?: number;
  /** On the workspace suppression list. */
  suppressed?: number;
  /** Up to three of them, by name. */
  suppressedNames?: string[];
  /** Gone from the workspace, or no LinkedIn profile to reach them on. */
  unreachable?: number;
}

function countOf(n: number, word: string): string {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * The toast after adding LD contacts to a campaign. Every contact left out is put under
 * its reason — a bare "0 contacts added" reads like something broke.
 */
export function describeContactsEnrollment(r: IContactsEnrollResult): { type: 'success' | 'warning' | 'info'; message: string } {
  const { enrolled, alreadyEnrolled = 0, suppressed = 0, suppressedNames = [], unreachable = 0 } = r;
  const skipped = alreadyEnrolled + suppressed + unreachable;

  const reasons = [
    suppressed ? `${countOf(suppressed, 'contact')} on the suppression list` : '',
    alreadyEnrolled ? `${countOf(alreadyEnrolled, 'contact')} already in this campaign` : '',
    unreachable ? `${countOf(unreachable, 'contact')} without a LinkedIn profile` : '',
  ].filter(Boolean);

  if (enrolled > 0) {
    return {
      type: 'success',
      message: `${countOf(enrolled, 'contact')} added to the campaign.`
        + (reasons.length ? ` Not added: ${reasons.join(', ')}.` : ''),
    };
  }

  // Nothing added, and the suppression list is the only reason.
  if (suppressed && suppressed === skipped) {
    if (suppressed === 1 && suppressedNames[0]) {
      return { type: 'warning', message: `${suppressedNames[0]} was not added: they are on the suppression list.` };
    }
    return {
      type: 'warning',
      message: suppressed === 1
        ? 'Not added: this contact is on the suppression list.'
        : `No contacts added: all ${suppressed.toLocaleString()} are on the suppression list.`,
    };
  }
  if (alreadyEnrolled && alreadyEnrolled === skipped) {
    return { type: 'info', message: 'All selected contacts are already in this campaign.' };
  }
  if (reasons.length) {
    return { type: suppressed ? 'warning' : 'info', message: `No contacts added: ${reasons.join(', ')}.` };
  }
  return { type: 'info', message: 'No contacts were added.' };
}

/** Campaigns owned by the current user that can still receive LD Contacts leads. */
export function filterEnrollableCampaigns(
  campaigns: ICampaign[],
  userId: string | undefined,
): ICampaign[] {
  if (!userId) return [];
  const uid = String(userId);
  return campaigns.filter(
    c => ENROLLABLE_STATUSES.includes(c.status) && String(c.createdBy) === uid,
  );
}
